// Analytics integration: Google Analytics + PostHog + Habita backend events
// Reads keys from env. Safe no-ops if keys are not set.

import { API_BASE } from './api';

// Support both env var names for GA4 measurement ID
const GA_ID =
  process.env.REACT_APP_GA4_MEASUREMENT_ID ||
  process.env.REACT_APP_GA_ID ||
  '';
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID || '';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

// Server-side event type constants (mirror /app/backend/analytics_service.py::EventTypes)
export const EventTypes = {
  PROPERTY_VIEWED: 'property.viewed',
  PROPERTY_SHARED: 'property.shared',
  PROPERTY_FAVORITED: 'property.favorited',
  PROPERTY_UNFAVORITED: 'property.unfavorited',
  PROPERTY_AGENT_CLICKED: 'property.agent_clicked',
  PROPERTY_AGENCY_CLICKED: 'property.agency_clicked',
  CONTACT_WHATSAPP: 'contact.whatsapp_clicked',
  CONTACT_PHONE: 'contact.phone_clicked',
  CONTACT_EMAIL: 'contact.email_clicked',
  AGENT_PROFILE_VIEWED: 'agent.profile_viewed',
  AGENT_WHATSAPP_CLICKED: 'agent.whatsapp_clicked',
  AGENT_PHONE_CLICKED: 'agent.phone_clicked',
  AGENT_AGENCY_CLICKED: 'agent.agency_clicked',
  AGENCY_PROFILE_VIEWED: 'agency.profile_viewed',
  AGENCY_WHATSAPP_CLICKED: 'agency.whatsapp_clicked',
  AGENCY_WEBSITE_CLICKED: 'agency.website_clicked',
  AGENCY_TEAM_MEMBER_CLICKED: 'agency.team_member_clicked',
  // Developers + Projects (Iter 29 demo)
  DEVELOPER_PROFILE_VIEWED: 'developer.profile_viewed',
  PROJECT_VIEWED: 'project.viewed',
  // Iter 32 — GA4 canonical events
  SEARCH: 'search',
  LEAD_CREATED: 'lead_created',
  // Iter 35 — non-dot events that fire only to third-party pixels (never to backend)
  SIGNUP_COMPLETED: 'signup_completed',
  // Valuation funnel (ValoraGratis · Fase 1 arriendo)
  VALUATION_STARTED: 'valuation.started',
  VALUATION_RESULT_VIEWED: 'valuation.result_viewed',
  VALUATION_PUBLISH_SELL_CLICKED: 'valuation.publish_sell_clicked',
  VALUATION_PUBLISH_RENT_CLICKED: 'valuation.publish_rent_clicked',
  VALUATION_PUBLISH_CREATED: 'valuation.publish_created',
};

// GA4-friendly alias map. Sending an alias in addition to the native event name
// gives clean snake_case events in GA4 dashboards without touching the backend
// (backend keeps receiving the dot-style events for /events/track).
const GA4_ALIAS = {
  'property.viewed': 'property_view',
  'property.favorited': 'property_favorite',
  'property.unfavorited': 'property_unfavorite',
  'property.shared': 'property_share',
  'property.agent_clicked': 'property_agent_click',
  'property.agency_clicked': 'property_agency_click',
  'contact.whatsapp_clicked': 'property_whatsapp_click',
  'contact.phone_clicked': 'property_phone_click',
  'contact.email_clicked': 'property_email_click',
  'agent.profile_viewed': 'agent_profile_view',
  'agent.whatsapp_clicked': 'agent_whatsapp_click',
  'agent.phone_clicked': 'agent_phone_click',
  'agent.agency_clicked': 'agent_agency_click',
  'agency.profile_viewed': 'agency_profile_view',
  'agency.whatsapp_clicked': 'agency_whatsapp_click',
  'agency.website_clicked': 'agency_website_click',
  'agency.team_member_clicked': 'agency_team_member_click',
  'developer.profile_viewed': 'developer_view',
  'project.viewed': 'project_view',
  'project.brochure_downloaded': 'project_brochure_download',
};

// Meta Pixel standard-event map. Maps Habita dot-namespaced events to
// Meta's canonical funnel events so the Events Manager auto-builds
// Custom Audiences (retargeting) + Custom Conversions without extra config.
// Events not in this map are still forwarded as `trackCustom` below.
const META_PIXEL_MAP = {
  'property.viewed': 'ViewContent',
  'project.viewed': 'ViewContent',
  'agent.profile_viewed': 'ViewContent',
  'agency.profile_viewed': 'ViewContent',
  'developer.profile_viewed': 'ViewContent',
  'contact.whatsapp_clicked': 'Contact',
  'contact.phone_clicked': 'Contact',
  'contact.email_clicked': 'Contact',
  'agent.whatsapp_clicked': 'Contact',
  'agent.phone_clicked': 'Contact',
  'agency.whatsapp_clicked': 'Contact',
  'property.favorited': 'AddToWishlist',
  // Non-dot Habita events that still map to Meta standard events
  search: 'Search',
  lead_created: 'Lead',
  signup_completed: 'CompleteRegistration',
};

export function getSessionId() {
  try {
    let s = sessionStorage.getItem('habita_sid');
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('habita_sid', s);
    }
    return s;
  } catch { return null; }
}

let posthogLoaded = false;
let gaLoaded = false;
let clarityLoaded = false;
let metaPixelLoaded = false;

export function initAnalytics() {
  if (typeof window === 'undefined') return;

  // ----- Google Analytics 4 -----
  if (GA_ID && !gaLoaded) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    // send_page_view:false — we emit page_view manually on route change to
    // avoid duplicate hits (React SPA doesn't reload between routes).
    window.gtag('config', GA_ID, { anonymize_ip: true, send_page_view: false });
    gaLoaded = true;
  }

  // ----- Microsoft Clarity (session recordings + heatmaps) -----
  // Loads async via the official Clarity bootstrap snippet — auto-tracks
  // page views, SPA route changes, clicks and scrolls. Fully non-blocking
  // and works alongside GA4 without conflicts.
  if (CLARITY_ID && !clarityLoaded) {
    /* eslint-disable */
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, 'clarity', 'script', CLARITY_ID);
    /* eslint-enable */
    clarityLoaded = true;
  }

  // ----- Meta Pixel (Facebook / Instagram Ads pixel) -----
  // Official snippet from Meta Events Manager. Bootstraps `window.fbq` with
  // an internal queue so calls made before the script actually loads are
  // replayed. Fires PageView on init; further PageViews are emitted from
  // trackPage() on each SPA route change. Fully non-blocking (async script).
  if (META_PIXEL_ID && !metaPixelLoaded) {
    /* eslint-disable */
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
    metaPixelLoaded = true;
  }

  // ----- PostHog -----
  if (POSTHOG_KEY && !posthogLoaded) {
    /* eslint-disable */
    !function (t, e) { var o, n, p, r; e.__SV || (window.posthog = e, e._i = [], e.init = function (i, s, a) { function g(t, e) { var o = e.split('.'); 2 == o.length && (t = t[o[0]], e = o[1]), t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } } (p = t.createElement('script')).type = 'text/javascript', p.async = !0, p.src = s.api_host + '/static/array.js', (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r); var u = e; for (void 0 !== a ? u = e[a] = [] : a = 'posthog', u.people = u.people || [], u.toString = function (t) { var e = 'posthog'; return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e }, u.people.toString = function () { return u.toString(1) + '.people (stub)' }, o = 'capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys'.split(' '), n = 0; n < o.length; n++)g(u, o[n]); e._i.push([i, s, a]) }, e.__SV = 1) }(document, window.posthog || []);
    window.posthog.init(POSTHOG_KEY, { api_host: POSTHOG_HOST });
    /* eslint-enable */
    posthogLoaded = true;
  }
}

export function trackPage(path) {
  if (typeof window === 'undefined') return;
  // Ensure GA/PostHog are initialised — idempotent, cheap after first call.
  // Guarantees cold-start attribution works even if a route change fires
  // before the App.js useEffect that calls initAnalytics() runs.
  if (!gaLoaded || !posthogLoaded || !clarityLoaded || !metaPixelLoaded) initAnalytics();
  const page_location = typeof window !== 'undefined' ? window.location.href : '';
  const page_title = typeof document !== 'undefined' ? document.title : '';
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location,
      page_title,
    });
  }
  if (window.posthog) window.posthog.capture('$pageview', { path });
  // Clarity auto-detects SPA route changes via History API, but explicitly
  // setting the current page as a custom tag improves heatmap segmentation
  // and filtering in the Clarity dashboard.
  if (window.clarity) {
    try { window.clarity('set', 'page_path', path); } catch (err) { /* clarity may not be fully initialised yet */ void err; }
  }
  // Meta Pixel — SPA route change PageView. Meta only auto-tracks the very
  // first PageView on script load; every subsequent navigation must be
  // emitted manually or Ads Manager under-counts sessions.
  if (window.fbq) {
    try { window.fbq('track', 'PageView'); } catch (err) { void err; }
  }

  // If the visitor arrived via an email campaign click, echo the event to GA4
  // (server already logged it in analytics_events when the redirect was hit).
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('habita_email_click') === '1' && !window.__habita_email_click_reported) {
      const campaign_id = params.get('utm_campaign') || 'unknown';
      // Only mark as reported once we actually dispatched — this way a cold
      // start where gtag isn't yet loaded will retry on the next route change.
      if (window.gtag) {
        window.gtag('event', 'email_campaign_click', {
          campaign_id,
          page_path: path,
        });
        window.__habita_email_click_reported = true;
      }
      if (window.posthog) {
        window.posthog.capture('email_campaign_click', { campaign_id, path });
        window.__habita_email_click_reported = true;
      }
    }
  } catch (err) {
    console.warn('email-click utm capture failed:', err?.message || err);
  }
}

export function track(event, props = {}) {
  if (typeof window === 'undefined' || typeof event !== 'string') return;

  // 1) Google Analytics 4 — send the native event AND its GA4-friendly alias
  //    (if one exists). Duplicates cost nothing but make dashboards cleaner.
  if (window.gtag) {
    window.gtag('event', event, props);
    const alias = GA4_ALIAS[event];
    if (alias && alias !== event) {
      window.gtag('event', alias, props);
    }
  }

  // 2) PostHog — always send the native event
  if (window.posthog) window.posthog.capture(event, props);

  // 2b) Microsoft Clarity — send as a custom event tag (searchable in Clarity)
  if (window.clarity) {
    try { window.clarity('event', event); } catch (err) { void err; }
  }

  // 2c) Meta Pixel — map to a standard event when possible (Events Manager
  //     recognises it automatically for retargeting/conversions), otherwise
  //     fall back to `trackCustom` so nothing is lost. Only forwards a small
  //     whitelisted subset of props to keep the pixel payload lean.
  if (window.fbq) {
    try {
      const std = META_PIXEL_MAP[event];
      const { property_id, agent_id, agency_id, project_id, developer_id, search_term, results_count } = props || {};
      const fbProps = {};
      if (property_id) { fbProps.content_ids = [property_id]; fbProps.content_type = 'property'; }
      else if (project_id) { fbProps.content_ids = [project_id]; fbProps.content_type = 'project'; }
      else if (agent_id) { fbProps.content_ids = [agent_id]; fbProps.content_type = 'agent'; }
      else if (agency_id) { fbProps.content_ids = [agency_id]; fbProps.content_type = 'agency'; }
      else if (developer_id) { fbProps.content_ids = [developer_id]; fbProps.content_type = 'developer'; }
      if (search_term) fbProps.search_string = search_term;
      if (typeof results_count === 'number') fbProps.num_items = results_count;
      if (std) window.fbq('track', std, fbProps);
      else window.fbq('trackCustom', event, fbProps);
    } catch (err) { void err; }
  }

  // 3) Habita backend — fire-and-forget for dot-namespaced events only
  //    (the whitelist in analytics_service.py rejects unknown events)
  if (event.includes('.')) {
    const { property_id, agent_id, agency_id, ...metadata } = props || {};
    const body = { event_type: event, session_id: getSessionId() };
    if (property_id) body.property_id = property_id;
    if (agent_id) body.agent_id = agent_id;
    if (agency_id) body.agency_id = agency_id;
    if (Object.keys(metadata).length) body.metadata = metadata;
    const token = (() => { try { return localStorage.getItem('habita_token'); } catch { return null; } })();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${API_BASE}/events/track`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch((err) => console.warn('analytics /events/track failed:', err?.message || err));
  }
}

export function identify(userId, traits = {}) {
  if (window.gtag) window.gtag('set', 'user_properties', traits);
  if (window.posthog) window.posthog.identify(userId, traits);
  if (window.clarity) {
    try {
      window.clarity('identify', userId);
      if (traits && Object.keys(traits).length) {
        Object.entries(traits).forEach(([k, v]) => {
          if (v != null) window.clarity('set', String(k), String(v));
        });
      }
    } catch (err) { void err; }
  }
}

export function resetIdentity() {
  if (window.posthog) window.posthog.reset();
}
